import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, Loader2, Navigation, Swords, GraduationCap, 
  CalendarDays, Users, Compass, Calendar, Trophy
} from 'lucide-react';
import { PrimaryCTA, UserSegment } from '@/hooks/useHomepagePersonalization';

interface PersonalizedHeroSectionProps {
  userName?: string;
  userSegment: UserSegment;
  primaryCTA: PrimaryCTA;
  ctaMessage: string;
  hasLocation: boolean;
  locationName: string | null;
  isLocating: boolean;
  onFindNearby: () => void;
  upcomingSessionCount?: number;
  activeEnrollmentCount?: number;
}

const SEGMENT_ICONS: Record<UserSegment, React.ReactNode> = {
  new_user: <Compass className="w-5 h-5" />,
  match_player: <Swords className="w-5 h-5" />,
  training_student: <GraduationCap className="w-5 h-5" />,
  court_booker: <CalendarDays className="w-5 h-5" />,
  club_member: <Users className="w-5 h-5" />,
  active_all: <Trophy className="w-5 h-5" />,
};

const SEGMENT_GREETINGS: Record<UserSegment, string> = {
  new_user: 'Chào mừng đến với SportRank! 👋',
  match_player: 'Sẵn sàng thi đấu! 🔥',
  training_student: 'Tiếp tục rèn luyện! 💪',
  court_booker: 'Tìm sân nhanh chóng! ⚡',
  club_member: 'Hoạt động CLB hôm nay! 🏆',
  active_all: 'Ngày mới năng động! 🌟',
};

export function PersonalizedHeroSection({
  userName,
  userSegment,
  primaryCTA,
  ctaMessage,
  hasLocation,
  locationName,
  isLocating,
  onFindNearby,
  upcomingSessionCount = 0,
  activeEnrollmentCount = 0,
}: PersonalizedHeroSectionProps) {
  const navigate = useNavigate();
  
  const greeting = SEGMENT_GREETINGS[userSegment];
  const icon = SEGMENT_ICONS[userSegment];
  
  const handlePrimaryCTA = () => {
    switch (primaryCTA) {
      case 'find_match':
        if (!hasLocation) {
          onFindNearby();
        } else {
          navigate('/matches');
        }
        break;
      case 'book_court':
        navigate('/courts');
        break;
      case 'join_class':
        navigate('/classes');
        break;
      case 'view_schedule':
        navigate('/my-training-schedule');
        break;
      case 'explore':
      default:
        if (!hasLocation) {
          onFindNearby();
        } else {
          navigate('/matches');
        }
        break;
    }
  };
  
  const getCTAButtonContent = () => {
    switch (primaryCTA) {
      case 'find_match':
        return (
          <>
            {isLocating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : hasLocation ? (
              <Swords className="w-4 h-4 mr-2" />
            ) : (
              <Navigation className="w-4 h-4 mr-2" />
            )}
            {hasLocation ? 'Tìm trận đấu' : 'Bật định vị'}
          </>
        );
      case 'book_court':
        return (
          <>
            <CalendarDays className="w-4 h-4 mr-2" />
            Đặt sân ngay
          </>
        );
      case 'join_class':
        return (
          <>
            <GraduationCap className="w-4 h-4 mr-2" />
            Tìm lớp học
          </>
        );
      case 'view_schedule':
        return (
          <>
            <Calendar className="w-4 h-4 mr-2" />
            Xem lịch học ({upcomingSessionCount})
          </>
        );
      case 'explore':
      default:
        return (
          <>
            {isLocating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Compass className="w-4 h-4 mr-2" />
            )}
            Khám phá ngay
          </>
        );
    }
  };
  
  return (
    <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-accent/10 to-background border border-border/50 p-4">
      {/* Background decoration */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-accent/10 rounded-full blur-2xl" />
      
      <div className="relative">
        {/* Greeting row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 text-primary">
              {icon}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {userName ? `Xin chào, ${userName}!` : 'Xin chào!'}
              </p>
              <h2 className="font-bold text-foreground text-lg">{greeting}</h2>
            </div>
          </div>
          
          {/* Location badge */}
          {hasLocation && locationName && (
            <Badge variant="secondary" className="text-xs">
              <MapPin className="w-3 h-3 mr-1" />
              {locationName}
            </Badge>
          )}
        </div>
        
        {/* Contextual info for training students */}
        {userSegment === 'training_student' && upcomingSessionCount > 0 && (
          <div className="bg-background/60 rounded-lg p-3 mb-3 border border-border/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {upcomingSessionCount} buổi học sắp tới
                </span>
              </div>
              {activeEnrollmentCount > 0 && (
                <Badge variant="outline" className="text-xs">
                  {activeEnrollmentCount} lớp đang học
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {/* CTA message */}
        <p className="text-sm text-muted-foreground mb-4">
          {ctaMessage}
        </p>
        
        {/* CTA buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={handlePrimaryCTA}
            className="bg-gradient-to-r from-primary to-accent hover:opacity-90 flex-1"
            disabled={isLocating}
          >
            {getCTAButtonContent()}
          </Button>
          
          {/* Secondary action based on segment */}
          {userSegment === 'new_user' && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/profile')}
              className="flex-shrink-0"
            >
              Hoàn tất hồ sơ
            </Button>
          )}
          
          {userSegment === 'match_player' && !hasLocation && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/matches/create')}
              className="flex-shrink-0"
            >
              Tạo trận
            </Button>
          )}
          
          {userSegment === 'training_student' && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/classes')}
              className="flex-shrink-0"
            >
              Tìm lớp mới
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
